import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addDays, isToday, addWeeks, subWeeks,
  parseISO, startOfDay,
} from 'date-fns';
import { Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, TrendingUp, CircleDollarSign, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import SessionDialog from '@/components/admin/SessionDialog';
import AdminTabs from '@/components/admin/AdminTabs';
import { logger } from '@/lib/logger';

const statusDot = (s) => ({
  New: 'bg-white/50',
  Confirmed: 'bg-[var(--pg-admin-accent)]',
  Active: 'bg-green-400',
  Completed: 'bg-green-500',
  Expired: 'bg-red-400',
  Cancelled: 'bg-white/25',
})[s] || 'bg-white/40';

const statusPill = (s) => ({
  New: 'bg-white/10 text-white/85 border-white/15',
  Confirmed: 'bg-[var(--pg-admin-accent-soft)] text-[var(--pg-admin-accent)] border-[var(--pg-admin-accent-ring)]',
  Active: 'bg-green-500/15 text-green-300 border-green-500/30',
  Completed: 'bg-green-500/25 text-green-200 border-green-500/40',
  Expired: 'bg-red-500/15 text-red-300 border-red-500/30',
  Cancelled: 'bg-white/5 text-white/60 border-white/10',
}[s] || 'bg-white/5 text-white/60 border-white/10');

const money = (v) => `SAR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const clientLabel = (s) => {
  const names = Array.isArray(s.client_names) && s.client_names.length ? s.client_names : (s.client_name ? [s.client_name] : []);
  if (names.length === 0) return '—';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
};

export default function AdminCalendar() {
  const [view, setView] = useState('month'); // 'month' | 'week' | 'agenda'
  const [cursor, setCursor] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultStart, setDefaultStart] = useState(null);

  const range = useMemo(() => {
    if (view === 'week') {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      return { start: s, end: endOfWeek(cursor, { weekStartsOn: 1 }) };
    }
    if (view === 'agenda') {
      const s = startOfDay(cursor);
      return { start: s, end: addDays(s, 30) };
    }
    const s = startOfMonth(cursor);
    const e = endOfMonth(cursor);
    return { start: startOfWeek(s, { weekStartsOn: 1 }), end: endOfWeek(e, { weekStartsOn: 1 }) };
  }, [cursor, view]);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/sessions', {
        params: { start: range.start.toISOString(), end: range.end.toISOString() },
      });
      setSessions(data || []);
    } catch (err) { logger.warn('Failed to load sessions', err); toast.error('Failed to load sessions'); }
    finally { setLoading(false); }
  }, [range]);

  const loadClients = useCallback(async () => {
    try { const { data } = await api.get('/admin/clients'); setClients(data || []); }
    catch (err) { logger.warn('Failed to load clients', err); }
  }, []);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get('/admin/revenue/summary'); setSummary(data); }
    catch (err) { logger.warn('Failed to load revenue summary', err); }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadClients(); loadSummary(); }, [loadClients, loadSummary]);

  const sessionsByDay = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      const key = format(parseISO(s.start_time), 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    map.forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [sessions]);

  const openNew = (dateHint) => { setEditing(null); setDefaultStart(dateHint || new Date()); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setDefaultStart(null); setDialogOpen(true); };
  const onSaved = () => { loadSessions(); loadSummary(); };

  const title = view === 'week'
    ? `${format(startOfWeek(cursor, { weekStartsOn: 1 }), 'MMM d')} – ${format(endOfWeek(cursor, { weekStartsOn: 1 }), 'MMM d, yyyy')}`
    : view === 'agenda' ? `Next 30 days from ${format(cursor, 'MMM d, yyyy')}`
    : format(cursor, 'MMMM yyyy');

  const shift = (dir) => {
    if (view === 'week') setCursor((c) => (dir > 0 ? addWeeks(c, 1) : subWeeks(c, 1)));
    else if (view === 'agenda') setCursor((c) => addDays(c, dir * 30));
    else setCursor((c) => (dir > 0 ? addMonths(c, 1) : subMonths(c, 1)));
  };

  return (
    <div data-testid={ADMIN.calendarPage}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><CalendarIcon className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Calendar</h1>
          <p className="mt-1 text-sm text-white/60">Plan sessions, track revenue, and manage your coaching schedule.</p>
        </div>
        <button
          data-testid={ADMIN.calendarNewSession}
          onClick={() => openNew()}
          className="inline-flex items-center gap-2 bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold px-4 py-2.5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" /> New session
        </button>
      </div>

      {/* KPIs — 2-col on mobile, 4-col on large */}
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
        <Kpi label="This Week" value={money(summary?.week?.revenue_total)} sub={`${summary?.week?.session_count || 0} sessions`} icon={TrendingUp} tone="accent" />
        <Kpi label="This Month" value={money(summary?.month?.revenue_total)} sub={`${summary?.month?.session_count || 0} sessions`} icon={CircleDollarSign} />
        <Kpi label="Outstanding" value={money(summary?.outstanding_all_time)} sub="unpaid invoices" icon={AlertCircle} tone="danger" />
        <Kpi label="Upcoming" value={summary?.upcoming_next_7_days || 0} sub="next 7 days" icon={CalendarIcon} />
      </div>

      {/* Controls */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid={ADMIN.calendarPrev} onClick={() => shift(-1)} className="p-2 rounded-lg border border-white/10 hover:bg-white/5" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid={ADMIN.calendarToday} onClick={() => setCursor(new Date())} className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 text-xs">Today</button>
          <button data-testid={ADMIN.calendarNext} onClick={() => shift(1)} className="p-2 rounded-lg border border-white/10 hover:bg-white/5" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
          <div className="ml-2 font-display text-lg sm:text-xl">{title}</div>
        </div>
        <AdminTabs
          options={[
            { id: 'month', label: 'Month', testid: ADMIN.calendarViewMonth },
            { id: 'week', label: 'Week', testid: ADMIN.calendarViewWeek },
            { id: 'agenda', label: 'Agenda', testid: ADMIN.calendarViewAgenda },
          ]}
          value={view}
          onChange={setView}
        />
      </div>

      {loading ? <div className="mt-4 text-sm text-white/60">Loading...</div> : (
        <div className="mt-4">
          {view === 'month' && <MonthView cursor={cursor} range={range} sessionsByDay={sessionsByDay} onDayClick={openNew} onSessionClick={openEdit} />}
          {view === 'week' && <WeekView cursor={cursor} sessions={sessions} onSlotClick={openNew} onSessionClick={openEdit} />}
          {view === 'agenda' && <AgendaView sessions={sessions} onSessionClick={openEdit} />}
        </div>
      )}

      <SessionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={onSaved}
        existing={editing}
        clients={clients}
        defaultStart={defaultStart}
      />
    </div>
  );
}

const Kpi = ({ label, value, sub, icon: Icon, tone = 'default' }) => {
  const toneClass = {
    default: 'bg-white/5 text-white',
    accent: 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]',
    danger: 'bg-red-500/15 text-red-300',
  }[tone];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#141414] p-3 sm:p-4 flex items-center gap-2.5 sm:gap-3">
      <div className={`inline-flex w-9 h-9 sm:w-10 sm:h-10 items-center justify-center rounded-xl shrink-0 ${toneClass}`}><Icon className="w-4 h-4 sm:w-5 sm:h-5" /></div>
      <div className="min-w-0">
        <div className="font-display text-base sm:text-xl leading-none truncate">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{label}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5 hidden sm:block">{sub}</div>}
      </div>
    </div>
  );
};

const MonthView = ({ cursor, range, sessionsByDay, onDayClick, onSessionClick }) => {
  const days = eachDayOfInterval({ start: range.start, end: range.end });
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
      <div className="grid grid-cols-7 border-b border-white/5">
        {dayNames.map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-widest text-white/50 border-r border-white/5 last:border-r-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const items = sessionsByDay.get(key) || [];
          const outside = !isSameMonth(day, cursor);
          const today = isToday(day);
          return (
            <button
              key={key}
              onClick={() => onDayClick(new Date(day.getFullYear(), day.getMonth(), day.getDate(), 10, 0))}
              className={`min-h-[92px] sm:min-h-[110px] text-left border-r border-b border-white/5 last:border-r-0 p-1.5 sm:p-2 relative ${outside ? 'bg-black/20 text-white/40' : 'bg-transparent hover:bg-white/[0.03]'} transition-colors`}
            >
              <div className={`text-xs font-semibold ${today ? 'inline-flex w-6 h-6 items-center justify-center rounded-full bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)]' : 'text-white/70'}`}>{format(day, 'd')}</div>
              <div className="mt-1 space-y-1">
                {items.slice(0, 3).map((s) => (
                  <div
                    key={s.id}
                    onClick={(e) => { e.stopPropagation(); onSessionClick(s); }}
                    className="cursor-pointer rounded-md bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 px-1.5 py-0.5 text-[10px] sm:text-[11px] truncate flex items-center gap-1"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(s.status)}`} />
                    <span className="text-white/85 shrink-0">{format(parseISO(s.start_time), 'HH:mm')}</span>
                    <span className="text-white/70 truncate">{s.session_name || clientLabel(s)}</span>
                  </div>
                ))}
                {items.length > 3 && <div className="text-[10px] text-white/50">+{items.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const WeekView = ({ cursor, sessions, onSlotClick, onSessionClick }) => {
  const weekStart = startOfWeek(cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
  const HOUR_START = 7;
  const HOUR_END = 22;
  const hours = Array.from({ length: HOUR_END - HOUR_START + 1 }).map((_, i) => HOUR_START + i);
  const PX_PER_MIN = 0.9; // 54px per hour

  return (
    <div className="rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] border-b border-white/5">
            <div />
            {days.map((d) => (
              <div key={d.toISOString()} className={`px-2 py-2 text-center border-l border-white/5 ${isToday(d) ? 'bg-[var(--pg-admin-accent-soft)]' : ''}`}>
                <div className="text-[10px] uppercase tracking-widest text-white/50">{format(d, 'EEE')}</div>
                <div className={`text-sm font-semibold ${isToday(d) ? 'text-[var(--pg-admin-accent)]' : 'text-white/85'}`}>{format(d, 'd')}</div>
              </div>
            ))}
          </div>
          <div className="relative grid grid-cols-[64px_repeat(7,minmax(0,1fr))]">
            <div>
              {hours.map((h) => (
                <div key={h} className="h-[54px] border-b border-white/5 text-[10px] text-white/40 pr-2 text-right pt-0.5">{`${String(h).padStart(2, '0')}:00`}</div>
              ))}
            </div>
            {days.map((d) => {
              const daySessions = sessions.filter((s) => isSameDay(parseISO(s.start_time), d));
              return (
                <div key={d.toISOString()} className="relative border-l border-white/5">
                  {hours.map((h) => (
                    <div
                      key={h}
                      onClick={() => onSlotClick(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0))}
                      className={`h-[54px] border-b border-white/5 hover:bg-white/[0.03] cursor-pointer ${isToday(d) ? 'bg-white/[0.02]' : ''}`}
                    />
                  ))}
                  {daySessions.map((s) => {
                    const start = parseISO(s.start_time);
                    const minsFromStart = (start.getHours() - HOUR_START) * 60 + start.getMinutes();
                    const top = Math.max(0, minsFromStart * PX_PER_MIN);
                    const height = Math.max(24, (s.duration_min || 60) * PX_PER_MIN - 2);
                    return (
                      <button
                        key={s.id}
                        onClick={(e) => { e.stopPropagation(); onSessionClick(s); }}
                        className="absolute left-1 right-1 rounded-lg border border-white/15 bg-[var(--pg-admin-accent-soft)] hover:brightness-125 backdrop-blur-sm px-1.5 py-1 text-left overflow-hidden"
                        style={{ top, height }}
                      >
                        <div className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(s.status)}`} />
                          <span className="text-[10px] text-white/80">{format(start, 'HH:mm')}</span>
                        </div>
                        <div className="text-[11px] font-semibold text-white truncate">{s.session_name || clientLabel(s)}</div>
                        {height > 40 && <div className="text-[10px] text-white/70 truncate">{clientLabel(s)}</div>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const AgendaView = ({ sessions, onSessionClick }) => {
  const groups = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      const key = format(parseISO(s.start_time), 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [sessions]);

  if (groups.length === 0) return <div className="rounded-2xl border border-white/10 bg-[#141414] p-8 text-center text-white/60">No sessions in this range.</div>;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#141414] overflow-hidden divide-y divide-white/5">
      {groups.map(([key, list]) => {
        const d = parseISO(key);
        return (
          <div key={key}>
            <div className="px-4 py-2 bg-white/[0.03] text-[11px] uppercase tracking-widest text-white/60 flex items-center justify-between">
              <span>{format(d, 'EEEE, MMM d')}</span>
              <span className="text-white/40">{list.length} session{list.length === 1 ? '' : 's'}</span>
            </div>
            <ul className="divide-y divide-white/5">
              {list.map((s) => (
                <li key={s.id}>
                  <button onClick={() => onSessionClick(s)} className="w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors flex items-center gap-3">
                    <div className="font-display text-xl text-[var(--pg-admin-accent)] w-16 shrink-0">{format(parseISO(s.start_time), 'HH:mm')}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-white truncate">{s.session_name || s.session_type}</span><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${statusPill(s.status)}`}>{s.status}</span></div>
                      <div className="text-xs text-white/60 mt-0.5 truncate">{clientLabel(s)} · {s.duration_min} min{s.location ? ` · ${s.location}` : ''}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-white">{money(s.total_price ?? s.price)}</div>
                      {(s.client_ids || []).length > 1 && <div className="text-[10px] uppercase tracking-widest text-white/40">{money(s.price_per_client)}/ea</div>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};
